import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const WithdrawalManager = ({ availableBalance, minimumWithdrawal, pendingWithdrawals }) => {
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [accountDetails, setAccountDetails] = useState('');
  const [showForm, setShowForm] = useState(false);
  
  // Campos adicionales para Transferencia Bancaria
  const [bankEntity, setBankEntity] = useState('');
  const [accountType, setAccountType] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [holderIdNumber, setHolderIdNumber] = useState('');

  const canWithdraw = availableBalance >= minimumWithdrawal;

  const paymentMethodOptions = [
    { value: 'nequi', label: 'Nequi' },
    { value: 'bank_transfer', label: 'Transferencia Bancaria' }
  ];

  const colombianBanks = [
    { value: 'bancolombia', label: 'Bancolombia' },
    { value: 'banco_bogota', label: 'Banco de Bogotá' },
    { value: 'davivienda', label: 'Davivienda' },
    { value: 'bbva', label: 'BBVA Colombia' },
    { value: 'banco_popular', label: 'Banco Popular' },
    { value: 'banco_occidente', label: 'Banco de Occidente' },
    { value: 'banco_caja_social', label: 'Banco Caja Social' },
    { value: 'banco_av_villas', label: 'Banco AV Villas' },
    { value: 'colpatria', label: 'Scotiabank Colpatria' },
    { value: 'banco_agrario', label: 'Banco Agrario' },
    { value: 'itau', label: 'Itaú' },
    { value: 'bancoomeva', label: 'Bancoomeva' },
    { value: 'otros', label: 'Otros' }
  ];

  const accountTypeOptions = [
    { value: 'ahorros', label: 'Ahorros' },
    { value: 'corriente', label: 'Corriente' }
  ];

  const handleSubmitWithdrawal = (e) => {
    e?.preventDefault();
    if (!canWithdraw) return;
    
    const withdrawalData = {
      amount: withdrawalAmount,
      method: paymentMethod,
      timestamp: new Date()?.toISOString()
    };

    // Agregar datos según el método de pago
    if (paymentMethod === 'nequi') {
      withdrawalData.accountNumber = accountDetails;
    } else if (paymentMethod === 'bank_transfer') {
      withdrawalData.bankEntity = bankEntity;
      withdrawalData.accountType = accountType;
      withdrawalData.accountHolderName = accountHolderName;
      withdrawalData.holderIdNumber = holderIdNumber;
      withdrawalData.accountNumber = accountDetails;
    }
    
    console.log('Solicitud de retiro para panel de administración:', withdrawalData);
    
    // Resetear formulario
    setShowForm(false);
    setWithdrawalAmount('');
    setPaymentMethod('');
    setAccountDetails('');
    setBankEntity('');
    setAccountType('');
    setAccountHolderName('');
    setHolderIdNumber('');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'approved': { color: 'bg-success text-success-foreground', icon: 'CheckCircle', label: 'Aprobado' },
      'pending': { color: 'bg-warning text-warning-foreground', icon: 'Clock', label: 'Pendiente' },
      'processing': { color: 'bg-primary text-primary-foreground', icon: 'RefreshCw', label: 'Procesando' },
      'rejected': { color: 'bg-error text-error-foreground', icon: 'XCircle', label: 'Rechazado' }
    };
    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={14} />
        {config?.label}
      </span>
    );
  };

  return (
    <div className="bg-card rounded-lg shadow-card p-6 border border-border">
      <h2 className="text-2xl font-bold text-foreground mb-6">Gestión de Retiros</h2>
      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 p-6 rounded-lg mb-6 border border-primary/20">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Saldo Disponible</p>
            <p className="text-3xl font-bold text-foreground">${availableBalance?.toLocaleString('es-CO')}</p>
            <p className="text-xs text-muted-foreground mt-1">COP disponibles para retiro</p>
          </div>
          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
            <Icon name="Wallet" size={32} color="white" />
          </div>
        </div>

        {canWithdraw ? (
          <Button
            variant="default"
            fullWidth
            onClick={() => setShowForm(!showForm)}
            iconName={showForm ? 'X' : 'ArrowDownToLine'}
            iconPosition="left"
          >
            {showForm ? 'Cancelar Solicitud' : 'Solicitar Retiro'}
          </Button>
        ) : (
          <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Saldo Insuficiente</p>
              <p className="text-xs text-muted-foreground mt-1">
                Necesitas ${minimumWithdrawal?.toLocaleString('es-CO')} COP mínimo para retirar.\n
                Te faltan ${(minimumWithdrawal - availableBalance)?.toLocaleString('es-CO')} COP.
              </p>
            </div>
          </div>
        )}
      </div>
      {showForm && canWithdraw && (
        <form onSubmit={handleSubmitWithdrawal} className="bg-muted/30 p-6 rounded-lg mb-6 space-y-4">
          <h3 className="text-lg font-semibold text-foreground mb-4">Nueva Solicitud de Retiro</h3>
          
          <Input
            type="number"
            label="Monto a Retirar"
            placeholder="Ingresa el monto"
            value={withdrawalAmount}
            onChange={(e) => setWithdrawalAmount(e?.target?.value)}
            required
            min={minimumWithdrawal}
            max={availableBalance}
            description={`Monto mínimo: $${minimumWithdrawal?.toLocaleString('es-CO')} COP`}
          />

          <Select
            label="Método de Pago"
            placeholder="Selecciona un método"
            options={paymentMethodOptions}
            value={paymentMethod}
            onChange={(value) => {
              setPaymentMethod(value);
              // Limpiar campos al cambiar método
              setAccountDetails('');
              setBankEntity('');
              setAccountType('');
              setAccountHolderName('');
              setHolderIdNumber('');
            }}
            required
          />

          {paymentMethod === 'nequi' && (
            <Input
              type="text"
              label="Número de Cuenta Nequi"
              placeholder="Ingresa el número de teléfono"
              value={accountDetails}
              onChange={(e) => setAccountDetails(e?.target?.value)}
              required
              description="Número de teléfono asociado a tu cuenta Nequi"
            />
          )}

          {paymentMethod === 'bank_transfer' && (
            <>
              <Select
                label="Entidad Bancaria"
                placeholder="Selecciona el banco"
                options={colombianBanks}
                value={bankEntity}
                onChange={setBankEntity}
                required
              />

              <Select
                label="Tipo de Cuenta"
                placeholder="Selecciona el tipo"
                options={accountTypeOptions}
                value={accountType}
                onChange={setAccountType}
                required
              />

              <Input
                type="text"
                label="Nombre del Titular de la Cuenta"
                placeholder="Nombre completo del titular"
                value={accountHolderName}
                onChange={(e) => setAccountHolderName(e?.target?.value)}
                required
                description="Nombre completo como aparece en la cuenta"
              />

              <Input
                type="text"
                label="Número de Identificación del Titular"
                placeholder="Cédula o NIT"
                value={holderIdNumber}
                onChange={(e) => setHolderIdNumber(e?.target?.value)}
                required
                description="Número de cédula o NIT del titular"
              />

              <Input
                type="text"
                label="Número de Cuenta"
                placeholder="Número de cuenta bancaria"
                value={accountDetails}
                onChange={(e) => setAccountDetails(e?.target?.value)}
                required
                description="Número de cuenta bancaria para la transferencia"
              />
            </>
          )}

          {paymentMethod && (
            <Button
              type="submit"
              variant="default"
              fullWidth
              iconName="Send"
              iconPosition="left"
            >
              Enviar Solicitud
            </Button>
          )}
        </form>
      )}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Retiros Pendientes</h3>
        {pendingWithdrawals?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground bg-muted/30 rounded-lg">
            <Icon name="Inbox" size={48} className="mx-auto mb-2 opacity-50" />
            <p>No tienes retiros pendientes</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pendingWithdrawals?.map((withdrawal) => (
              <div key={withdrawal?.id} className="bg-muted/30 p-4 rounded-lg border border-border">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="ArrowDownToLine" size={20} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">${withdrawal?.amount?.toLocaleString('es-CO')} COP</p>
                      <p className="text-xs text-muted-foreground">{withdrawal?.date}</p>
                    </div>
                  </div>
                  {getStatusBadge(withdrawal?.status)}
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Método: {withdrawal?.method}</span>
                  <span className="text-muted-foreground">ID: {withdrawal?.id}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WithdrawalManager;